import React, { useRef, useState, useEffect, useLayoutEffect, useMemo } from "react";
import {
  DefaultTooltipRenderer,
  WordCloud,
  AnimatedWordRenderer,
} from "@isoterik/react-word-cloud";
import { FiDownload, FiCloud } from "react-icons/fi";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";

/* ================================================================
 *  Tooltip سفارشی
 * ================================================================ */
const TooltipRenderer = (data, t) => (
  <DefaultTooltipRenderer
    data={data}
    placement="bottom"
    transform={false}
    containerStyle={{
      borderRadius: "12px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "2px",
      minWidth: "110px",
      padding: "0.5rem 0.75rem",
      background: "rgba(15, 23, 42, 0.92)",
      border: "1px solid rgba(251, 191, 36, 0.4)",
      boxShadow: "0 8px 30px -10px rgba(0,0,0,0.6)",
      backdropFilter: "blur(10px)",
      fontFamily: "Yekan, Vazir, sans-serif",
    }}
    textStyle={{
      fontFamily: "'Yekan', Vazir, sans-serif",
      fontSize: "13px",
      color: "#FCD34D",
      fontWeight: 700,
    }}
  >
    <div style={{ color: "#5EEAD4", fontSize: "11px" }}>
      {t("keywordCloud.tooltip.count")} {data.word?.realValue}
    </div>
  </DefaultTooltipRenderer>
);

/* ================================================================
 *  KeywordCloud
 * ================================================================ */
export default function KeywordCloud({ wordCloudData = [] }) {
  const { t } = useTranslation(["user"]);

  // ref روی div داخلی (بدون padding کارت) تا عرض واقعی اندازه‌گیری بشه
  const innerRef = useRef(null);
  // ابعاد null شروع می‌شن → تا اندازه‌گیری نشده، WordCloud رندر نمی‌شه
  const [dimensions, setDimensions] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  /* ---------- min / max مقادیر ---------- */
  const { minValue, maxValue } = useMemo(() => {
    const vals = wordCloudData.map((w) => Number(w.value) || 1);
    return {
      minValue: vals.length ? Math.min(...vals) : 1,
      maxValue: vals.length ? Math.max(...vals) : 1,
    };
  }, [wordCloudData]);

  /* ---------- Responsive flags ---------- */
  useEffect(() => {
    const checkSize = () => setIsMobile(window.innerWidth < 640);
    checkSize();
    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, []);

  /* ---------- اندازه‌گیری واقعی با ResizeObserver ---------- */
  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;

    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) {
        setDimensions((prev) => {
          // برای داده‌های زیاد ارتفاع بیشتر می‌دیم تا کلمات جا بشن
          const ratio = wordCloudData.length > 60 ? 0.9 : 0.6;
          const h = Math.round(w * ratio);
          // فقط وقتی واقعاً تغییر کرده آپدیت کن → جلوگیری از رندر اضافه
          if (prev && prev.width === w && prev.height === h) return prev;
          return { width: w, height: h };
        });
      }
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);

    return () => ro.disconnect();
  }, [wordCloudData.length]);

  /* ---------- کلمات + اندازه فونت (متناسب با تعداد و فضای موجود) ---------- */
  const formattedWords = useMemo(() => {
    const minFont = isMobile ? 12 : 16;
    const maxFont = isMobile ? 38 : 60;
    const PAD = 4;

    // عدد بین ۰ تا ۱؛ ریشهٔ دوم باعث می‌شه کلمات کم‌تکرار هم قابل‌تشخیص بمونند
    const norm = (v) =>
      maxValue === minValue
        ? 0.5
        : Math.sqrt((v - minValue) / (maxValue - minValue));

    const base = [...wordCloudData]
      .map((item) => ({ ...item, num: Number(item.value) || 1 }))
      .sort((a, b) => b.num - a.num)
      .map((item, index) => {
        const n = norm(item.num);
        return {
          text: item.text,
          value: item.num,
          realValue: item.value,
          norm: n,
          rawFont: minFont + n * (maxFont - minFont),
          animationDelay: index * 80,
        };
      });

    // تا اندازه‌گیری نشده، مقیاس ۱
    let scale = 1;

    if (dimensions) {
      const budget = dimensions.width * dimensions.height * 0.4;

      // تخمین مساحت: عرض هر حرف ≈ ۰٫۵۵ فونت، ارتفاع خط ≈ ۱٫۲۵ فونت
      const totalArea = (s) =>
        base.reduce((sum, w) => {
          const f = Math.max(9, w.rawFont * s);
          return (
            sum + (w.text.length * 0.55 * f + PAD * 2) * (f * 1.25 + PAD * 2)
          );
        }, 0);

      while (scale > 0.3 && totalArea(scale) > budget) scale -= 0.05;
    }

    return base.map((w) => ({ ...w, fontPx: Math.max(9, w.rawFont * scale) }));
  }, [wordCloudData, isMobile, minValue, maxValue, dimensions]);

  const fontSize = (word) => word.fontPx;

  const fontWeight = (word) => {
    if (word.norm > 0.75) return "900";
    if (word.norm > 0.5) return "700";
    if (word.norm > 0.25) return "600";
    return "500";
  };

  /* ---------- گرادیانت‌ها ---------- */
  const gradients = useMemo(
    () => [
      {
        id: "kw-gradient-gold",
        type: "linear",
        angle: 45,
        stops: [
          { offset: "0%", color: "#FCD34D" },
          { offset: "100%", color: "#F59E0B" },
        ],
      },
      {
        id: "kw-gradient-teal",
        type: "linear",
        angle: 45,
        stops: [
          { offset: "0%", color: "#5EEAD4" },
          { offset: "100%", color: "#0D9488" },
        ],
      },
      {
        id: "kw-gradient-mix",
        type: "linear",
        angle: 135,
        stops: [
          { offset: "0%", color: "#FCD34D" },
          { offset: "50%", color: "#5EEAD4" },
          { offset: "100%", color: "#14B8A6" },
        ],
      },
    ],
    []
  );

  const resolveFill = (_word, index) => {
    const idx = index % 3;
    return `url(#${gradients[idx].id})`;
  };

  /* ---------- رندر کلمه با انیمیشن ---------- */
  const renderWord = (data, ref) => (
    <AnimatedWordRenderer
      ref={ref}
      data={data}
      animationDelay={(_word, index) => index * 80}
    />
  );

  /* ---------- رندر Tooltip با t ---------- */
  const renderTooltip = (data) => TooltipRenderer(data, t);

  /* ---------- دانلود اکسل ---------- */
  const downloadWordCloudExcel = () => {
    if (!wordCloudData?.length) return;
    const ws = XLSX.utils.json_to_sheet(
      wordCloudData.map((i) => ({
        [t("keywordCloud.download.columnWord")]: i.text,
        [t("keywordCloud.download.columnCount")]: i.value,
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("keywordCloud.download.sheetName"));
    XLSX.writeFile(wb, t("keywordCloud.download.fileName"));
  };

  /* ---------- حالت بارگذاری ---------- */
  const showPlaceholder = !dimensions || formattedWords.length === 0;

  /* ================================================================
   *  RENDER
   * ================================================================ */
  return (
    <div
      className="
        relative w-full h-full overflow-hidden
        rounded-2xl
        bg-gradient-to-br from-teal-700 to-teal-900
        dark:!bg-gradient-to-br dark:!from-slate-900 dark:!via-[#0B2E2F] dark:!to-slate-900
        backdrop-blur-xl
        border border-teal-500/20 dark:!border-teal-400/15
        shadow-[0_10px_40px_-20px_rgba(15,118,110,0.45)]
        dark:!shadow-[0_10px_40px_-20px_rgba(0,0,0,0.85)]
        p-4 sm:p-5
        transition-colors duration-500
      "
    >
      <div className="pointer-events-none absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />
      <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-amber-300/15 blur-3xl dark:!bg-amber-400/10" />
      <div className="pointer-events-none absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-teal-400/15 blur-3xl dark:!bg-teal-500/10" />

      {/* Header */}
      <div className="relative flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="shrink-0 flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-amber-300/30 to-teal-400/20 border border-amber-300/40">
            <FiCloud className="text-amber-400" size={15} />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-amber-100 truncate">
              {t("keywordCloud.title")}
            </h3>
            <p className="text-[11px] text-slate-400 truncate">
              {t("keywordCloud.subtitle")}
            </p>
          </div>
        </div>

        <div className="relative group shrink-0">
          <button
            onClick={downloadWordCloudExcel}
            disabled={!wordCloudData?.length}
            aria-label={t("keywordCloud.download.aria")}
            className="
      flex items-center justify-center
      h-9 w-9 rounded-xl
      text-teal-700 dark:!text-amber-200
      bg-white/50 dark:!bg-white/5
      border border-teal-500/20 dark:!border-teal-400/20
      backdrop-blur-md
      transition-all duration-300
      hover:scale-105 active:scale-95
      hover:border-amber-300/60 hover:text-amber-600 dark:hover:!text-amber-200
      hover:bg-amber-300/10
      disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100
    "
          >
            <FiDownload className="w-4 h-4" />
          </button>

          {/* Tooltip پایین دکمه */}
          <div
            className="
      absolute top-full mt-2 left-1/2 -translate-x-1/2
      whitespace-nowrap rounded-md
      bg-slate-900/95 text-white text-[10px] font-medium
      py-1 px-2
      border border-amber-300/30
      opacity-0 group-hover:opacity-100
      -translate-y-1 group-hover:translate-y-0
      pointer-events-none
      transition-all duration-200
      shadow-lg z-50
    "
          >
            {t("keywordCloud.download.tooltip")}
          </div>
        </div>
      </div>

      {/* WordCloud / Placeholder */}
      <div ref={innerRef} className="relative">
        {showPlaceholder ? (
          <div
            className="flex flex-col items-center justify-center gap-3 text-slate-400 dark:!text-slate-500"
            style={{ height: dimensions?.height || 260 }}
          >
            <div className="h-12 w-12 rounded-full border-2 border-dashed border-teal-400/40 flex items-center justify-center">
              <FiCloud size={20} className="animate-pulse text-teal-400/70" />
            </div>
            <p className="text-sm animate-pulse">
              {dimensions
                ? t("keywordCloud.empty")
                : t("keywordCloud.loading")}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden" style={{ height: dimensions.height }}>
            <WordCloud
              key={`${dimensions.width}x${dimensions.height}`} // ← رندر مجدد کامل با تغییر ابعاد
              words={formattedWords}
              width={dimensions.width}
              height={dimensions.height}
              enableTooltip
              renderTooltip={renderTooltip}
              font={() => "Yekan"}
              fontSize={fontSize}
              fontWeight={fontWeight}
              spiral="archimedean"
              padding={3}
              rotate={() => 0}
              random={false}
              renderWord={renderWord}
              gradients={gradients}
              fill={resolveFill}
            />
          </div>
        )}
      </div>
    </div>
  );
}